import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { ApolloProvider, ApolloClient } from '@apollo/client'
import { logout as babylonLogout, setAuthConfig } from '@babylon/babylon-auth'
import { BabylonIntl } from '@babylon/intl'
import { useAuth0 } from '@auth0/auth0-react'

import { init as initApolloClient } from '@/config/apolloClient'
import config from '@/config'
import { getInitialLocale, getMessagesOnLocaleChange } from '@/i18n'
import { useConfig, useContent } from '@/redux/partnerConfig/selectors'
import { useOidcRegistration } from './redux/productConfig/selectors'
import {
  setUserPartnerSlug,
  resetUserConfig,
  setSignupProgress,
} from '@/redux/userConfig/actions'
import { useUserLocale, useUserPartnerSlug } from '@/redux/userConfig/selectors'
import { getFeatureFlags, Loading } from '@/components'
import { authTokenStorageKey, idTokenStorageKey } from '@/constants'
import { IntlGlobalProvider } from '@/utils'
import App from './App'
import { resetUserProfile } from '@/redux/userProfile/actions'
import { getPersistor } from '@/config/redux'

const { GRAPHQL_ENDPOINT } = config
const apolloUri = `${GRAPHQL_ENDPOINT}/api`

const AppWrapper = () => {
  const { appName, acceptLanguage: partnerDefaultLanguage } = useConfig()
  const content = useContent()
  const userLocale = useUserLocale()
  const dispatch = useDispatch()
  const existingPartnerSlug = useUserPartnerSlug()
  const { partnerUrlSlug } = useConfig()
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    getIdTokenClaims,
    logout,
  } = useAuth0()
  const oidcEnabled = useOidcRegistration()
  const [token, setToken] = useState(
    sessionStorage.getItem(authTokenStorageKey)
  )
  const getToken = async () => {
    const authToken = await getAccessTokenSilently()

    if (authToken) {
      setToken(authToken)
    }
  }

  useEffect(() => {
    if (token) {
      sessionStorage.setItem(authTokenStorageKey, token)
      getIdTokenClaims().then((idToken) => {
        if (idToken) {
          // eslint-disable-next-line no-underscore-dangle
          sessionStorage.setItem(idTokenStorageKey, idToken?.__raw)
        }
      })
    }
  }, [getIdTokenClaims, token])

  useEffect(() => {
    const isSamePartner = existingPartnerSlug === partnerUrlSlug

    if (existingPartnerSlug && !isSamePartner) {
      dispatch(resetUserConfig())
    }

    dispatch(setUserPartnerSlug(partnerUrlSlug))

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerUrlSlug])

  useEffect(() => {
    if (isAuthenticated) {
      getToken()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  if (isLoading) {
    return <Loading />
  }

  const logoutPath = `${window.location.origin}/callback`

  const availableLocales = Object.keys(content)

  const initialLocale = getInitialLocale(
    availableLocales,
    partnerDefaultLanguage,
    userLocale
  )

  const apolloClient: ApolloClient<any> = initApolloClient(
    apolloUri,
    appName,
    initialLocale,
    oidcEnabled
      ? () => {
          sessionStorage.clear()
          dispatch(resetUserProfile())
          getPersistor().purge()
          logout({ returnTo: logoutPath })
        }
      : () => {
          babylonLogout({ redirect: false })
          sessionStorage.clear()
          dispatch(resetUserProfile())
          dispatch(setSignupProgress(0))
          // Reloading the window, the signup orchestrator will redirect the user to the
          // beginning of the current flow and will avoid redireciton loops
          window.location.reload()
        }
  )

  setAuthConfig({
    axiosOpts: () => ({
      headers: {
        'accept-language': initialLocale,
      },
    }),
  })

  // Get feature flags set in cookies and alert the user they are applied
  getFeatureFlags()

  return (
    <BabylonIntl
      initialLocale={initialLocale}
      getMessagesOnLocaleChange={(locale: string) =>
        getMessagesOnLocaleChange({ locale })
      }
    >
      <IntlGlobalProvider>
        <ApolloProvider client={apolloClient}>
          <App />
        </ApolloProvider>
      </IntlGlobalProvider>
    </BabylonIntl>
  )
}

export default AppWrapper
